import { IFileCipher, IFileMeta } from '@/contracts/modules/note/cipher/IFileCipher'
import { EnumNoteError } from '@/contracts/modules/note/enum/EnumNoteError'
import { GCM_IV_BYTES, decryptGcm, encryptGcm } from '@/helpers/aesGcm'
import { base64UrlToBytes, bytesToBase64Url } from '@/helpers/base64url'

const KEY_ALGORITHM: AesKeyGenParams = { name: 'AES-GCM', length: 256 }
const KEY_USAGES: KeyUsage[] = ['encrypt', 'decrypt']
/** Метка AES-GCM: на столько шифротекст куска длиннее открытого текста */
export const GCM_TAG_BYTES = 16
/** Первые четыре байта IV сведений: не пересекается с номерами кусков */
const META_IV_PREFIX = 0xFFFFFFFF

function chunkIv(index: number): Uint8Array {
  const iv = new Uint8Array(GCM_IV_BYTES)
  new DataView(iv.buffer).setBigUint64(4, BigInt(index), false)
  return iv
}

function chunkAad(index: number, count: number): Uint8Array {
  return new TextEncoder().encode(`chunk:${index}/${count}`)
}

const META_AAD = new TextEncoder().encode('meta')

function isMeta(value: unknown): value is IFileMeta {
  const meta = value as IFileMeta
  return typeof meta === 'object' && meta !== null && typeof meta.name === 'string' && typeof meta.path === 'string' && typeof meta.mime === 'string'
}

export class FileCipher implements IFileCipher {
  createKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(KEY_ALGORITHM, true, KEY_USAGES)
  }

  encryptChunk(key: CryptoKey, index: number, count: number, bytes: Uint8Array): Promise<Uint8Array> {
    return encryptGcm(key, chunkIv(index), chunkAad(index, count), bytes)
  }

  async decryptChunk(key: CryptoKey, index: number, count: number, cipher: Uint8Array): Promise<Uint8Array> {
    try {
      return await decryptGcm(key, chunkIv(index), chunkAad(index, count), cipher)
    } catch {
      throw new Error(EnumNoteError.CANNOT_DECRYPT)
    }
  }

  async encryptMeta(key: CryptoKey, meta: IFileMeta): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES))
    new DataView(iv.buffer).setUint32(0, META_IV_PREFIX, false)
    const text = JSON.stringify({ name: meta.name, path: meta.path, mime: meta.mime })
    const cipher = await encryptGcm(key, iv, META_AAD, new TextEncoder().encode(text))
    const packed = new Uint8Array(iv.length + cipher.length)
    packed.set(iv)
    packed.set(cipher, iv.length)

    return bytesToBase64Url(packed)
  }

  async decryptMeta(key: CryptoKey, cipher: string): Promise<IFileMeta> {
    let meta: unknown
    try {
      const packed = base64UrlToBytes(cipher)
      const bytes = await decryptGcm(key, packed.slice(0, GCM_IV_BYTES), META_AAD, packed.slice(GCM_IV_BYTES))
      meta = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes))
    } catch {
      throw new Error(EnumNoteError.CANNOT_DECRYPT)
    }
    if (!isMeta(meta)) {
      throw new Error(EnumNoteError.CANNOT_DECRYPT)
    }

    return { name: meta.name, path: meta.path, mime: meta.mime }
  }
}
